// Lightweight API client for the Sales Analytics backend.
//
// In development the Vite dev server proxies `/api/*` → http://localhost:5000
// (see vite.config.js), so by default we use relative URLs.
//
// To point at a different host (e.g. production), define VITE_API_BASE_URL:
//   VITE_API_BASE_URL=https://api.your-domain.com

import axios from 'axios';

const BASE = `${process.env.REACT_APP_API_URL.replace(/\/+$/, '')}`;

function toQuery(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" || v === "All") return;
    usp.append(k, v);
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
}

async function request(path, params) {
  const url = `${BASE}${path}${toQuery(params)}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status} ${res.statusText} — ${text || url}`);
  } 
  return res.json();
}

export const api = {
  filters:           ()                => request("/api/analytics/filters"),
  filterCustomers:   (family)          => request("/api/analytics/customers", family ? { family } : undefined),
  kpis:              (params)          => request("/api/analytics/kpis", params),
  salesByChannel:    (params)          => request("/api/analytics/sales-by-channel", params),  
  customerRanking:   (params)          => request("/api/analytics/customer-ranking", { limit: 20, ...params }),
  customerProducts:  (customerName, params) => request("/api/analytics/customer-products", { ...params, customer: customerName }),
  productRanking:    (params)          => request("/api/analytics/product-ranking", { limit: 50, ...params }),
  salesByFormat:     (params)          => request("/api/analytics/sales-by-format", params),
  salesByCategory:   (params)          => request("/api/analytics/sales-by-category", params), 
  salesByRegion:     (params)          => request("/api/analytics/sales-by-region", params),  
  salesByProductFamily: (params)       => request("/api/analytics/sales-by-product-family", params),    
  forecast:          (params)          => request("/api/analytics/forecast", { months: 6, ...params }),

  // ✅ NEW: Customer Family Drill-Down → shows individual customers within a family
  customerFamilyDrilldown: (familyName, params = {}) =>
    request(`/api/analytics/customer-family-drilldown/${encodeURIComponent(familyName)}`, params),

  // ✅ NEW: Customer Orders Drill-Down → shows all orders for a specific customer
  customerOrders: (customerName, params = {}) =>
    request(`/api/analytics/customer-orders/${encodeURIComponent(customerName)}`, params),

  // ---- Product Family Management (AUTO-derived from ERPAG rawMaterialSku) ----
  productFamilies: {
    list:          (params) => request("/api/product-families", params),
    drill:         (rawMaterialSku) => request(`/api/product-families/${encodeURIComponent(rawMaterialSku)}`),
    salesByFamily: (params) => request("/api/product-families/analytics/sales", params),

    // Optional product-master maintenance
    listProducts:  (q = "") => request("/api/product-families/products/list", { q }),
    updateProduct: (sku, body) =>
      fetch(`${BASE}/api/product-families/products/${encodeURIComponent(sku)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      }).then(handleJson),
  },

  // ---- Category Family Management ----
  categoryFamilies: {
    list:        ()       => request("/api/category-families"),
    categories:  (q = "") => request("/api/category-families/categories", { q }),

    create: (body) =>
      fetch(`${BASE}/api/category-families`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      }).then(handleJson),

    update: (id, body) =>
      fetch(`${BASE}/api/category-families/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      }).then(handleJson),

    assign: (category, familyId) =>
      fetch(`${BASE}/api/category-families/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ category, familyId }),
      }).then(handleJson),

    remove: (id) =>
      fetch(`${BASE}/api/category-families/${id}`, {
        method: "DELETE",
        headers: { Accept: "application/json" },
      }).then(handleJson),
  },

  // ---- Format Management ----
  formats: {
    list:         ()                       => request("/api/format-mappings"),
    get:          (id)                     => request(`/api/format-mappings/${id}`),
    unassigned:   (q = "")                 => request("/api/format-mappings/unassigned", { q }),
    categories:   (q = "")                 => request("/api/format-mappings/categories", { q }),
    salesByFormat:(params)                 => request("/api/format-mappings/analytics/sales", params),

    assign: (category, formatId, createBody) =>
      fetch(`${BASE}/api/format-mappings/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ category, formatId, ...(createBody || {}) }),
      }).then(handleJson),

    create: (body) =>
      fetch(`${BASE}/api/format-mappings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      }).then(handleJson),

    update: (id, body) =>
      fetch(`${BASE}/api/format-mappings/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      }).then(handleJson),

    addCategories: (id, categories) =>
      fetch(`${BASE}/api/format-mappings/${id}/categories`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ categories }),
      }).then(handleJson),

    removeCategory: (id, category) =>
      fetch(`${BASE}/api/format-mappings/${id}/categories/${encodeURIComponent(category)}`, {
        method: "DELETE",
        headers: { Accept: "application/json" },
      }).then(handleJson),

    remove: (id) =>
      fetch(`${BASE}/api/format-mappings/${id}`, {
        method: "DELETE",
        headers: { Accept: "application/json" },
      }).then(handleJson),
  },

  // ---- Customer Family Management ----
  families: {
    list:         ()                       => request("/api/customer-families"),
    get:          (id)                     => request(`/api/customer-families/${id}`),
    unassigned:   (q = "")                 => request("/api/customer-families/unassigned", { q }),
    salesByFamily:(params)                 => request("/api/customer-families/analytics/sales", params),

    create: (body) =>
      fetch(`${BASE}/api/customer-families`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      }).then(handleJson),

    update: (id, body) =>
      fetch(`${BASE}/api/customer-families/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      }).then(handleJson),

    addMembers: (id, members) =>
      fetch(`${BASE}/api/customer-families/${id}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ members }),
      }).then(handleJson),

    removeMember: (id, customer) =>
      fetch(`${BASE}/api/customer-families/${id}/members/${encodeURIComponent(customer)}`, {
        method: "DELETE",
        headers: { Accept: "application/json" },
      }).then(handleJson),

    merge: (sourceIds, targetId, newName) =>
      fetch(`${BASE}/api/customer-families/merge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ sourceIds, targetId, newName }),
      }).then(handleJson),

    remove: (id) =>
      fetch(`${BASE}/api/customer-families/${id}`, {
        method: "DELETE",
        headers: { Accept: "application/json" },
      }).then(handleJson),
  },

  // ---- Weekly Planner
  OperationsPlanner: {

    downloadErpagSO: async ({ orderId, soNumber }) => {
      const baseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, "");
      const params = new URLSearchParams({
        type: "so",
        orderId: orderId || "",
        soNumber: soNumber || "",
      });

      const response = await fetch(
        `${baseUrl}/api/weeklyplanner/erpag/download?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Accept: "application/pdf, application/json",
          },
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to download SO (${response.status})`);
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        return await response.json();
      }
      return await response.blob();
    },

    downloadErpagPackingList: async ({ orderId, soNumber, packingListNumber }) => {
      const baseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, "");
      const params = new URLSearchParams({
        type: "pl",
        plid: packingListNumber || "",
      });

      const response = await fetch(
        `${baseUrl}/api/weeklyplanner/erpag/download?${params.toString()}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/pdf, application/json",
          },
          body: JSON.stringify({
            type: "pl",
            orderId: orderId || "",
            soNumber: soNumber || "",
            packingListNumber: packingListNumber || "",
            plid: packingListNumber || "",
          }),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(
          errData.message || `Failed to download Packing List (${response.status})`
        );
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        return await response.json();
      }
      return await response.blob();
    },

    listFamilyGroups: async () => {
      const res = await fetch(`${process.env.REACT_APP_API_URL.replace(/\/+$/, '')}/api/weeklyplanner/family-groups`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      return res.json();
    },

    createFamilyGroup: async (payload) => {
      const res = await fetch(`${process.env.REACT_APP_API_URL.replace(/\/+$/, '')}/api/weeklyplanner/family-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      return res.json();
    },

    updateFamilyGroup: async (id, payload) => {
      const res = await fetch(`${process.env.REACT_APP_API_URL.replace(/\/+$/, '')}/api/weeklyplanner/family-groups/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      return res.json();
    },

    deleteFamilyGroup: async (id) => {
      const res = await fetch(`${process.env.REACT_APP_API_URL.replace(/\/+$/, '')}/api/weeklyplanner/family-groups/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      return res.json();
    },

    list: () => request("/api/weeklyplanner/get-employees"),

    // AFTER (Fixed):
    completedHistory: async (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetch(`${BASE}/api/weeklyplanner/completed-history${query ? `?${query}` : ''}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // ...your other headers (e.g. Authorization)
        }
      });
    },

    searchHistory: async (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return fetch(`${BASE}/api/weeklyplanner/completed/search${query ? `?q${query}` : ''}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // ...your other headers (e.g. Authorization)
        }
      });
    },     

    create: (body) =>
      fetch(`${BASE}/api/weeklyplanner/add-employee`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      }).then(handleJson),

    update: (body) =>
      fetch(`${BASE}/api/weeklyplanner/orders/complete`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      }).then(handleJson), 
      
    delete: (body) =>
      fetch(`${BASE}/api/weeklyplanner/orders/delete`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      }).then(handleJson),       

    removeMember: (id, customer) =>
      fetch(`${BASE}/api/weeklyplanner/remove-employee/${id}`, {
        method: "DELETE",
        headers: { Accept: "application/json" },
      }).then(handleJson),

    importSoByOrderNumber: (body) =>
      fetch(`${BASE}/api/weeklyplanner/orders/import`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Accept: "application/json"
        },
        body: JSON.stringify(body),
      }).then(handleJson), 
      
    editSoById: (body) =>
      fetch(`${BASE}/api/weeklyplanner/orders/editSO`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Accept: "application/json"
        },
        body: JSON.stringify(body),
      }).then(handleJson),

    confirmReceived: (body) =>
      fetch(`${BASE}/api/weeklyplanner/orders/confirm-received`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Accept: "application/json"
        },
        body: JSON.stringify(body),
      }).then(handleJson),  

    handlePersistUpdate: (body) =>
      fetch(`${BASE}/api/weeklyplanner/orders/persist-update`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Accept: "application/json"
        },
        body: JSON.stringify(body),
      }).then(handleJson),                    
  },   
};

const Api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

function buildParams(filters = {}) {
  const params = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) params[k] = v;
  });
  return params;
}

// ── Revenue by Month ─────────────────────────────────────────
export async function fetchRevenueByMonth(filters = {}) {
  const { data } = await Api.get('/api/analytics/revenue-by-month', { params: buildParams(filters) });
  return data;
}

async function handleJson(res) {
  const text = await res.text();
  const data = text ? safeParse(text) : null;

  if (!res.ok) {
    const msg =
      (data && (data.error || data.message)) ||
      `${res.status} ${res.statusText}`;

    const err = new Error(msg);
    err.status = res.status;
    err.data = data;

    throw err;
  }

  return data;
}

function safeParse(t) { try { return JSON.parse(t); } catch { return null; } }

export const formatCurrency = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

export const formatNumber = (n) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.round(Number(n)) || 0);

// Units are always whole numbers — never show decimals
export const formatUnits = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Math.round(Number(n) || 0)
  );
